import moment from "moment";
import PhoneMask from "./phoneMask";
import { serialExcelMexico } from "./fechaHoraMexico";

/**
 * Estilos y constructores de celda compartidos por los Excel que se generan a
 * partir del `dataSat` de un documento (cotización, pedido, embarque, venta):
 * el de resumen de una selección (`ExcelDocumentosMasivo`) y el de un documento
 * suelto (`ExcelDocumentoIndividual`).
 */

// Formato Contabilidad: el "$" pegado al borde izquierdo y el número a la
// derecha. Es el estándar de todos los Excel de este proyecto.
export const FORMATO_CONTABILIDAD = '_-"$"* #,##0.00_-;-"$"* #,##0.00_-;_-"$"* "-"??_-;_-@_-';

export const border = {
  top: { style: "thin", color: { auto: 1 } },
  bottom: { style: "thin", color: { auto: 1 } },
  left: { style: "thin", color: { auto: 1 } },
  right: { style: "thin", color: { auto: 1 } },
};
export const headerStyle = {
  font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: "00BFD6" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border,
};
export const textStyle = { border, alignment: { vertical: "center" } };
export const numeroStyle = { border, alignment: { horizontal: "right" } };
export const moneyStyle = { border, alignment: { horizontal: "right" }, numFmt: FORMATO_CONTABILIDAD };
export const dateStyle = { border, alignment: { horizontal: "center" } };
export const horaStyle = { ...dateStyle, numFmt: "hh:mm" };
export const fechaHoraStyle = { ...dateStyle, numFmt: "dd/mm/yyyy hh:mm" };
export const totalTextoStyle = { ...textStyle, font: { bold: true } };
export const totalMoneyStyle = { ...moneyStyle, font: { bold: true } };

// xlsx-js-style no infiere el tipo de celda cuando ya se le pasa un objeto {v,s}
// armado: sin "t" explícito escribe el número como texto y Excel no puede
// aplicarle el formato de moneda.
export const celdaMoneda = (valor, estilo = moneyStyle) =>
  (valor || valor === 0) && !Number.isNaN(Number(valor))
    ? { v: Number(valor), t: "n", s: estilo }
    : { v: "", s: estilo };

export const celdaTexto = (valor, estilo = textStyle) => ({
  v: valor === undefined || valor === null ? "" : valor,
  s: estilo,
});

export const celdaNumero = (valor, estilo = numeroStyle) =>
  (valor || valor === 0) && !Number.isNaN(Number(valor))
    ? { v: Number(valor), t: "n", s: estilo }
    : celdaTexto(valor, estilo);

export const celdaFecha = (valor, estilo = dateStyle) => {
  const m = moment(valor);
  if (!valor || !m.isValid()) return celdaTexto(valor, estilo);
  // Anclada al mediodía UTC para que no se corra de día al abrirla en otra zona.
  return {
    v: new Date(Date.UTC(m.year(), m.month(), m.date(), 12, 0, 0)),
    t: "d",
    z: "dd/mm/yyyy",
    s: estilo,
  };
};

// Instantes (createdAt...) en hora de México, como número de serie de Excel: así la
// celda es una hora de verdad (se ordena, se filtra y se resta), no texto.
const celdaSerialMexico = (valor, estilo, conFecha) => {
  const serial = serialExcelMexico(valor);
  if (serial === null) return celdaTexto("", estilo);
  return {
    v: conFecha ? serial : serial - Math.floor(serial),
    t: "n",
    z: estilo.numFmt,
    s: estilo,
  };
};

/** Sólo la hora (hh:mm) de un instante, en hora de México. */
export const celdaHora = (valor, estilo = horaStyle) => celdaSerialMexico(valor, estilo, false);

/** Fecha y hora (dd/mm/yyyy hh:mm) de un instante, en hora de México. */
export const celdaFechaHora = (valor, estilo = fechaHoraStyle) =>
  celdaSerialMexico(valor, estilo, true);

export const aNumero = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

export const sumarImportes = (arreglo) =>
  (arreglo || []).reduce((acumulado, valor) => acumulado + aNumero(valor?.Importe), 0);

/**
 * Los impuestos locales viven en el complemento, fuera del nodo `Impuestos`,
 * pero sí cuentan para el Total: si no se suman, el Excel no cuadra con el PDF.
 */
export const nodoImpuestosLocales = (dataSat) => {
  const nodo = dataSat?.Complemento?.Any?.find((valueComp) => valueComp["implocal:ImpuestosLocales"]);
  return nodo ? nodo["implocal:ImpuestosLocales"] : null;
};

export const impuestosLocales = (dataSat) => {
  const locales = nodoImpuestosLocales(dataSat);
  return {
    traslados: aNumero(locales?.["@TotaldeTraslados"]),
    retenciones: aNumero(locales?.["@TotaldeRetenciones"]),
  };
};

export const nombreVendedor = (vendedor) =>
  vendedor
    ? [vendedor.nombre, vendedor.apellidoPaterno, vendedor.apellidoMaterno]
      .filter(Boolean)
      .join(" ")
    : "";

/**
 * Encabezado de la empresa emisora, tal como lo arma el backend
 * (`obtenerEmpresaCompleta` / los controladores de reportes): la dirección viene
 * repartida en calle, colonia, municipio, estado y país.
 */
export const direccionEmpresa = (empresa) => [
  [empresa?.calle, empresa?.numeroCalle, empresa?.numeroInterior].filter(Boolean).join(" "),
  [
    [empresa?.colonia?.tipoAsentamiento, empresa?.colonia?.nombre].filter(Boolean).join(" "),
    empresa?.colonia?.cp ? "C.P. " + empresa.colonia.cp : "",
    empresa?.municipio?.nombre,
    empresa?.estado?.abreviatura,
    empresa?.pais?.abreviatura,
  ].filter(Boolean).join(" "),
].filter(Boolean).join(", ");

export const contactoEmpresa = (empresa) => {
  const partes = [];
  if (empresa?.telefono) partes.push("Tel: " + PhoneMask(empresa.telefono));
  if (empresa?.correo) partes.push(empresa.correo);
  return partes.join("  |  ");
};

export const nombreEmpresa = (empresa) => {
  if (empresa?.nombreComercial) return empresa.nombreComercial;
  if (empresa?.persona) return empresa.nombre || "";
  return [empresa?.nombre, empresa?.apellidoPaterno, empresa?.apellidoMaterno].filter(Boolean).join(" ");
};
